/* eslint-disable react-hooks/exhaustive-deps */
import { AlertCircle, CheckCircle, FileText, List, Loader, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkerSelector from '../components/WorkerSelector';
import invoiceService from '../services/invoiceService';
import './WorkerInvoices.css';

function firstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function lastDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
}

const WorkerInvoices = () => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [startDate, setStartDate] = useState(firstDayOfMonth());
  const [endDate, setEndDate] = useState(lastDayOfMonth());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => { loadWorkers(); }, []);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      const res = await invoiceService.getWorkerPaymentProfiles();
      if (res && res.profiles) {
        setWorkers(res.profiles);
      }
    } catch (err) {
      setError('Errore caricamento percettori: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedWorker) return setError('Seleziona un percettore');
    if (!startDate || !endDate) return setError('Seleziona le date del periodo');
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const response = await invoiceService.previewInvoice({
        workerPaymentProfileId: selectedWorker,
        periodType: 'CUSTOM',
        periodStart: startDate,
        periodEnd: endDate,
      });
      if (response.success) setPreview(response.preview);
      else setError(response.message || 'Anteprima fallita');
    } catch (err) {
      setError('Errore anteprima: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedWorker) return setError('Seleziona un percettore');
    if (!window.confirm('Generare la fattura? Verra inviata ad Acube.')) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const response = await invoiceService.generateInvoice({
        workerPaymentProfileId: selectedWorker,
        periodType: 'CUSTOM',
        periodStart: startDate,
        periodEnd: endDate,
      });
      if (response.success) {
        setSuccess('Fattura ' + response.invoice.invoiceNumber + ' generata con successo!');
        setPreview(null);
        if (response.invoice.acube && !response.invoice.acube.sent) {
          setError('Fattura creata ma invio ad Acube fallito: ' + response.invoice.acube.error);
        }
      } else {
        setError(response.message || 'Generazione fallita');
      }
    } catch (err) {
      setError('Errore generazione: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (cents) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  const fmtDate = (s) => new Date(s).toLocaleDateString('it-IT');
  const selObj = workers.find(w => w.workerPaymentProfileId === selectedWorker);

  const applyPreset = (type) => {
    if (type === 'current') {
      setStartDate(firstDayOfMonth());
      setEndDate(lastDayOfMonth());
    } else {
      const d = new Date();
      const y = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
      const m = d.getMonth() === 0 ? 12 : d.getMonth();
      setStartDate(`${y}-${String(m).padStart(2, '0')}-01`);
      setEndDate(new Date(y, m, 0).toISOString().split('T')[0]);
    }
    setPreview(null);
  };

  return (
    <div className="wi-page">

      <div className="wi-header">
        <div className="wi-header-left">
          <div className="wi-header-icon"><FileText size={22} /></div>
          <div>
            <h1 className="wi-title">Genera Fattura Percettore</h1>
            <p className="wi-subtitle">Emetti la fattura per le commissioni di un percettore nel periodo selezionato</p>
          </div>
        </div>
        <button className="wi-btn-ghost" onClick={() => navigate('/admin/invoices')}>
          <List size={14} /> Lista Fatture
        </button>
      </div>

      {error && (
        <div className="wi-alert wi-alert-error">
          <AlertCircle size={15} />
          <span>{error}</span>
          <button className="wi-alert-close" onClick={() => setError(null)}>x</button>
        </div>
      )}
      {success && (
        <div className="wi-alert wi-alert-success">
          <CheckCircle size={15} />
          <span>{success}</span>
          <button className="wi-alert-close" onClick={() => setSuccess(null)}>x</button>
        </div>
      )}

      <div className="wi-card">
        <WorkerSelector
          workers={workers}
          value={selectedWorker}
          onChange={(val) => { setSelectedWorker(val); setPreview(null); }}
          label="Percettore"
          placeholder="Cerca per nome o email..."
          required={true}
          disabled={loading}
        />
        <div className="wi-period-row">
          <div className="wi-presets">
            <button className="wi-preset-btn" onClick={() => applyPreset('current')} disabled={loading} type="button">
              Mese corrente
            </button>
            <button className="wi-preset-btn" onClick={() => applyPreset('last')} disabled={loading} type="button">
              Mese scorso
            </button>
          </div>
          <div className="wi-dates-inline">
            <div className="wi-field-inline">
              <label htmlFor="wi-start">Dal</label>
              <input
                id="wi-start"
                type="date"
                className="wi-input"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPreview(null); }}
                disabled={loading}
              />
            </div>
            <span className="wi-dates-sep">→</span>
            <div className="wi-field-inline">
              <label htmlFor="wi-end">Al</label>
              <input
                id="wi-end"
                type="date"
                className="wi-input"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPreview(null); }}
                disabled={loading}
              />
            </div>
          </div>
          <button
            className="wi-btn-secondary"
            onClick={handlePreview}
            disabled={loading || !selectedWorker}
          >
            {loading ? <Loader size={14} className="wi-spin" /> : <FileText size={14} />}
            Anteprima
          </button>
        </div>
      </div>

      {preview && (
        <div className="wi-card">
          <h2 className="wi-card-title">
            <span className="wi-step-num">2</span>
            Anteprima Fattura
          </h2>

          <div className="wi-preview-meta">
            <div className="wi-meta-item">
              <span className="wi-meta-label">Percettore</span>
              <span className="wi-meta-value">
                {selObj
                  ? selObj.firstName + ' ' + selObj.lastName + ' (' + selObj.email + ')'
                  : selectedWorker}
              </span>
            </div>
            <div className="wi-meta-item">
              <span className="wi-meta-label">Periodo</span>
              <span className="wi-meta-value">
                {fmtDate(preview.periodStart)} - {fmtDate(preview.periodEnd)}
              </span>
            </div>
            <div className="wi-meta-item">
              <span className="wi-meta-label">Transazioni totali</span>
              <span className="wi-meta-value">
                {preview.stats?.total ?? preview.tipTransfers?.length ?? 0}
              </span>
            </div>
            {preview.stats?.alreadyInvoiced > 0 && (
              <>
                <div className="wi-meta-item">
                  <span className="wi-meta-label">Non fatturate</span>
                  <span className="wi-badge wi-badge-green">{preview.stats.notInvoiced}</span>
                </div>
                <div className="wi-meta-item">
                  <span className="wi-meta-label">Gia fatturate</span>
                  <span className="wi-badge wi-badge-orange">{preview.stats.alreadyInvoiced}</span>
                </div>
              </>
            )}
          </div>

          {preview.reversedTransfers && preview.reversedTransfers.length > 0 && (
            <div className="wi-info-box">
              <strong>{preview.reversedTransfers.length} transazioni reversate da Stripe</strong>
              {' '}— escluse automaticamente dalla fattura.
            </div>
          )}

          {preview.warnings && preview.warnings.length > 0 && (
            <div className="wi-warn-box">
              <strong>Dati mancanti — completa prima di generare:</strong>
              <ul className="wi-warn-list">
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {preview.tipTransfers && preview.tipTransfers.length > 0 && (
            <div className="wi-table-wrap">
              <table className="wi-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Venue</th>
                    <th>Lordo</th>
                    <th>Commissioni</th>
                    <th>Netto</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.tipTransfers.map((t) => (
                    <tr key={t.id} className={t.alreadyInvoiced ? 'wi-row-muted' : ''}>
                      <td>{fmtDate(t.date)}</td>
                      <td>{t.venue}</td>
                      <td className="wi-num">{fmt(t.amountGross)}</td>
                      <td className="wi-num wi-fee">{fmt(t.feeTotal)}</td>
                      <td className="wi-num">{fmt(t.amountNet)}</td>
                      <td>
                        {t.alreadyInvoiced
                          ? <span className="wi-badge wi-badge-orange">Gia fatturata</span>
                          : <span className="wi-badge wi-badge-green">Da fatturare</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="wi-totals">
            {(() => {
              const tot = preview.stats?.alreadyInvoiced > 0
                ? preview.totalsNotInvoiced
                : preview.totals;
              const lbl = preview.stats?.alreadyInvoiced > 0
                ? 'Importo Fattura (' + preview.stats.notInvoiced + ' transazioni)'
                : 'Importo Fattura';
              return (
                <>
                  <div className="wi-total-row">
                    <span>Commissioni Stripe</span>
                    <span>{fmt(tot?.totalFeeStripe || 0)}</span>
                  </div>
                  <div className="wi-total-row">
                    <span>Commissioni SoonTip</span>
                    <span>{fmt(tot?.totalFeeSoontip || 0)}</span>
                  </div>
                  <div className="wi-total-row wi-total-highlight">
                    <span>{lbl}</span>
                    <strong>{fmt(tot?.totalFeeTotal || 0)}</strong>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="wi-form-actions">
            <button
              className="wi-btn-primary"
              onClick={handleGenerate}
              disabled={loading || !preview.canGenerate}
              title={!preview.canGenerate ? 'Tutte le transazioni sono gia state fatturate' : ''}
            >
              {loading ? <Loader size={14} className="wi-spin" /> : <Send size={14} />}
              {preview.canGenerate ? 'Genera e Invia ad Acube' : 'Nessuna transazione da fatturare'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerInvoices;
